import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../AuthContext';
import { Box, Button, Select, MenuItem, Modal, Typography, TextField, CircularProgress } from '@mui/material';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import axios from 'axios';
import FileUpload from '../Internal-Reports/FileUpload';
import DateSelector from "../Internal-Reports/DateSelector";
import { Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper } from '@mui/material';

function FactoryDataUpload() {
    const { user } = useContext(AuthContext);
    const [configurations, setConfigurations] = useState([]);
    const [selectedConfigIndex, setSelectedConfigIndex] = useState(null);
    const [selectedDate, setSelectedDate] = useState("");
    const [fileHeaders, setFileHeaders] = useState([]);
    const [fileData, setFileData] = useState([]);
    const [sanitizedData, setSanitizedData] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const apiUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axios.get(`${apiUrl}/api/ex_fac_data_settings`, { withCredentials: true });
                setConfigurations(response.data.configurations || []);
            } catch (error) {
                console.error('Error fetching settings:', error);
            }
        };

        if (user) {
            fetchSettings();
        }
    }, [user, apiUrl]);

    const handleSelectConfiguration = (index) => {
        setSelectedConfigIndex(index);
        setSanitizedData([]);
        setFileHeaders([]);
        setFileData([]);
        setErrorMessage("");
    };

    const parseFile = (file) => {
        const fileExtension = file.name.split('.').pop();
        if (fileExtension === 'csv') {
            parseCSV(file);
        } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
            parseExcel(file);
        } else {
            alert('Unsupported file format');
        }
    };

    const parseCSV = (file) => {
        if (!selectedDate) {
            setErrorMessage("Please select a date before uploading a file.");
            return;
        }
        Papa.parse(file, {
            complete: (result) => {
                const selectedConfig = configurations[selectedConfigIndex] || {};
                if (!selectedConfig) {
                    setErrorMessage("Please select a configuration first.");
                    return;
                }
    
                const rowStartIndex = selectedConfig.rowStart ? selectedConfig.rowStart - 1 : 0;
                setFileHeaders(result.data[rowStartIndex] || []);
                setFileData(result.data.slice(rowStartIndex + 1));
            },
            encoding: "UTF-8", // ✅ Explicit encoding handling
            header: false,
            skipEmptyLines: true,
            transform: (value) => value.replace(/[\u00A0\uFFFD]/g, "").trim() // ✅ Remove non-breaking spaces & replacement characters
        });
    };

    const parseExcel = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const excelData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
            const selectedConfig = configurations[selectedConfigIndex] || {};

            if (!selectedConfig) {
                setErrorMessage("Please select a configuration first.");
                return;
            }

            const rowStartIndex = selectedConfig.rowStart ? selectedConfig.rowStart - 1 : 0;
            setFileHeaders(excelData[rowStartIndex] || []);
            setFileData(excelData.slice(rowStartIndex + 1));
        };
        reader.readAsArrayBuffer(file);
    };

    const extractFactoryData = () => {
        if (selectedConfigIndex === null) {
            setErrorMessage("No configuration selected.");
            return;
        }
        
        if (!selectedDate) {
            setErrorMessage("Please select a date before extracting data.");
            return;
        }
        const selectedConfig = configurations[selectedConfigIndex];
    
        if (!selectedConfig || !selectedConfig.mappings) {
            setErrorMessage("Configuration mappings not found.");
            return;
        }
    
        if (!fileHeaders.length || !fileData.length) {
            setErrorMessage("Please upload a valid file before extracting data.");
            return;
        }
    
        /**
         * ✅ Format date from various formats to YYYY-MM-DD
         * Handles: DD/MM/YYYY, D/M/YYYY, DD.MM.YYYY, D.M.YYYY
         * Removes leading/trailing whitespaces and non-breaking spaces (\u00A0).
         */
        const formatDate = (dateStr) => {
            dateStr = dateStr.replace(/\u00A0/g, "").trim(); // ✅ Remove non-breaking spaces & trim
            const delimiter = dateStr.includes("/") ? "/" : dateStr.includes(".") ? "." : null;
            if (delimiter) {
                const parts = dateStr.split(delimiter);
                if (parts.length === 3) {
                    let [day, month, year] = parts.map((part) =>
                        part.trim().padStart(2, "0")
                    ); // Ensure 2-digit day/month

                    // 🔥 Convert 2-digit year → 4-digit year
                    if (year.length === 2) {
                        const currentYear = new Date().getFullYear().toString().slice(-2);
                        const century = year <= currentYear ? "20" : "19"; // assumes 00–currentYear = 2000s, else 1900s
                        year = century + year;
                    }
                    return `${year}-${month}-${day}`; // Return in YYYY-MM-DD format
                }
            }
            return dateStr; // Return original if unknown format
        };

        /**
         * ✅ Format date from various formats to YYYY-MM-DD
         * Handles: MM/DD/YYYY, M/D/YYYY, MM.DD.YYYY, M.D.YYYY
         * Removes leading/trailing whitespaces and non-breaking spaces (\u00A0).
         */
        const formatDate_two = (dateStr) => {
            dateStr = dateStr.replace(/\u00A0/g, "").trim(); // ✅ Remove non-breaking spaces & trim
    
            const delimiter = dateStr.includes("/") ? "/" : dateStr.includes(".") ? "." : null;
            if (delimiter) {
                const parts = dateStr.split(delimiter);
                if (parts.length === 3) {
                    let [month, day, year] = parts.map((part) =>
                        part.trim().padStart(2, "0")
                    ); // Ensure 2-digit day/month
                    return `${year}-${month}-${day}`; // Return in YYYY-MM-DD format
                }
            }
            return dateStr; // Return original if unknown format
        };
    
        const extracted = fileData
            .map((row) => {
                let extractedRow = {};
                let isValidRow = true;
    
                Object.keys(selectedConfig.mappings).forEach((dbField) => {
                    const csvHeader = selectedConfig.mappings[dbField];
                    const columnIndex = fileHeaders.indexOf(csvHeader);
    
                    if (columnIndex !== -1 && row[columnIndex] !== undefined && row[columnIndex] !== "") {
                        let value = row[columnIndex]
                            .toString()
                            .replace(/\u00A0/g, "") // ✅ Remove non-breaking spaces
                            .trim(); // ✅ Trim whitespace for all fields
    
                        // ✅ Convert to YYYY-MM-DD if it's a date field
                        if (dbField.toLowerCase() === "date" && /^(\d{1,2}[\/.]\d{1,2}[\/.]\d{2,4})$/.test(value)) {
                            value = formatDate(value);
                            // value = formatDate(selectedDate);
                        }
    
                        extractedRow[dbField] = value;
                    } else {
                        extractedRow[dbField] = "";
                        isValidRow = false;
                    }
                });
    
                return isValidRow ? extractedRow : null;
            })
            .filter(Boolean); // Remove invalid rows
    
        if (extracted.length === 0) {
            setErrorMessage("No valid data extracted. Please check the mappings and file.");
            setSanitizedData([]);
        } else {
            setSanitizedData(extracted);
            setErrorMessage("");
        }
    };      

    const handleDataChange = (rowIndex, field, value) => {
        if (field !== "brand") return;

        const updatedData = sanitizedData.map((row, index) =>
            index === rowIndex ? { ...row, [field]: value } : row
        );
        setSanitizedData(updatedData);
    };

    const BATCH_SIZE = 5;
    const saveToBackend = async () => {
        if (sanitizedData.length === 0) {
            setErrorMessage("No valid rows to save.");
            return;
        }
    
        setIsUploading(true); // ✅ Show overlay when upload starts
        const totalBatches = Math.ceil(sanitizedData.length / BATCH_SIZE);
        try {
            for (let i = 0; i < totalBatches; i++) {
                const batch = sanitizedData.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE);
                await axios.post(`${apiUrl}/api/save_sale`, { sales: batch }, { withCredentials: true });
            }
            setSuccessMessage(true);
        } catch (error) {
            console.error("Error saving data:", error);
            setErrorMessage("Error saving data. Please try again.");
        } finally {
            setIsUploading(false); // ✅ Hide overlay when done
        }
    };  

    const handleCloseSuccessModal = () => {
        setSuccessMessage(false);
        setSelectedConfigIndex(null);
        setSelectedDate("");
        setSanitizedData([]);
        setFileHeaders([]);
        setFileData([]);
    };
    
    return (
        <Box sx={{ padding: 3, minHeight: '100vh', maxWidth: "100%", overflowX: "hidden" }}>
            <h2>Factory Data Upload</h2>

            {configurations.length > 0 && (
                <div>
                    <h3>Select Configuration</h3>
                    <Select
                        value={selectedConfigIndex !== null ? selectedConfigIndex : ""}
                        onChange={(e) => handleSelectConfiguration(e.target.value)}
                        displayEmpty
                        sx={{ minWidth: 200 }}
                    >
                        <MenuItem value="" disabled>Select Configuration</MenuItem>
                        {configurations.map((config, index) => (
                            <MenuItem key={index} value={index}>
                                {config.name}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
            )}

            {selectedConfigIndex !== null && <DateSelector title="Select Date" selectedDate={selectedDate} onDateChange={setSelectedDate} />}

            {selectedConfigIndex !== null && selectedDate && (
                <div>
                    <FileUpload onFileUpload={parseFile} />
                    <Button onClick={extractFactoryData} variant="contained" sx={{ marginTop: 2 }}>
                        Extract Data
                    </Button>
                </div>
            )}
            {errorMessage && (
                <p>{errorMessage}</p>
            )}
            {sanitizedData.length > 0 && (
                <>
                    <TableContainer component={Paper} sx={{ width: "100%", overflowX: "auto" }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#f4f4f4" }}>
                                    {Object.keys(sanitizedData[0]).map((field) => (
                                        <TableCell key={field} sx={{ fontWeight: 'bold', textTransform: 'capitalize', padding: "10px" }}>
                                            {field.replace(/_/g, " ")}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sanitizedData.map((row, rowIndex) => (
                                    <TableRow key={rowIndex}>
                                        {Object.keys(row).map((field) => (
                                            <TableCell key={field} sx={{ padding: "10px", textAlign: "center", width: field === "brand" ? "200px" : "auto" }}>
                                                {field === "brand" ? (
                                                    <TextField 
                                                        value={row[field]} 
                                                        onChange={(e) => handleDataChange(rowIndex, field, e.target.value)} 
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{ fontSize: "14px", backgroundColor: "#fff" }}
                                                    />
                                                ) : (
                                                    row[field]
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Button onClick={saveToBackend} variant="contained" sx={{ marginTop: 2 }}>
                        Save to Backend
                    </Button>
                </>
            )}

            {isUploading && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        zIndex: 9999,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <CircularProgress size={80} thickness={4} />
                    <Typography sx={{ marginTop: 2, fontSize: "20px" }}>Uploading data, please wait...</Typography>
                </Box>
            )}

            <Modal open={successMessage} onClose={handleCloseSuccessModal}>
                <Box sx={{ p: 4, backgroundColor: "white", margin: "auto", textAlign: "center" }}>
                    <Typography variant="h6">Data Successfully Uploaded!</Typography>
                    <Button onClick={handleCloseSuccessModal} variant="contained" sx={{ marginTop: 2 }}>
                        OK
                    </Button>
                </Box>
            </Modal>
        </Box>
    );
}

export default FactoryDataUpload;
